import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodash.css';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrency(value, moeda = 'BRL') {
  const currency = String(moeda || 'BRL').toUpperCase();

  try {
    return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency,
    });
  } catch {
    return `${currency} ${formatMoney(value)}`;
  }
}

function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase();
}

function isStatusPago(status) {
  return normalizeStatus(status) === 'pago';
}

function isStatusAberto(status) {
  return ['aguardando pagamento', 'em aberto', 'pendente'].includes(
    normalizeStatus(status)
  );
}

function getValorPrincipal(pagamento) {
  if (isStatusPago(pagamento.status)) {
    return Number(pagamento.valor || 0);
  }

  if (isStatusAberto(pagamento.status)) {
    return Number(pagamento.valor_calculado ?? pagamento.valor ?? 0);
  }

  return Number(pagamento.valor || pagamento.valor_calculado || 0);
}

function getValorLabel(pagamento) {
  if (isStatusPago(pagamento.status)) return 'Recebido';
  if (isStatusAberto(pagamento.status)) return 'Previsto';
  if (normalizeStatus(pagamento.status) === 'isento') return 'Isento';
  return 'Registrado';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
}

function formatMesReferencia(value) {
  if (!value || !String(value).includes('-')) return value || '—';
  const [ano, mes] = String(value).split('-');
  return `${mes}/${ano}`;
}

function getMesAtualYYYYMM() {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  return `${ano}-${mes}`;
}

export default function ListarPagamentos() {
  const [pagamentos, setPagamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [status, setStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    try {
      setLoading(true);
      setErro('');

      const [p, c] = await Promise.all([
        api.get('/api/pagamentos'),
        api.get('/api/clientes'),
      ]);

      setPagamentos(p.data || []);
      setClientes(c.data || []);
    } catch (err) {
      console.error('Erro ao carregar pagamentos:', err);
      setErro('Erro ao carregar pagamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar().catch(console.error);
  }, []);

  const linhas = useMemo(() => {
    const termo = busca.toLowerCase();

    return pagamentos
      .map((p) => ({
        ...p,
        cliente: clientes.find(
          (c) => Number(c.id) === Number(p.cliente_id)
        ),
      }))
      .filter((p) => {
        const okCliente =
          !clienteId || String(p.cliente_id) === String(clienteId);

        const okStatus =
          status === 'todos' ||
          String(p.status || '').toLowerCase() === status.toLowerCase();

        const okBusca =
          !termo ||
          String(p.id || '').toLowerCase().includes(termo) ||
          String(p.cliente?.nome || '').toLowerCase().includes(termo) ||
          String(p.cliente?.email || '').toLowerCase().includes(termo) ||
          String(p.mes_referencia || '').toLowerCase().includes(termo) ||
          String(p.metodo_pagamento || '').toLowerCase().includes(termo) ||
          String(p.provedor_nome || '').toLowerCase().includes(termo) ||
          String(p.provedor_codigo || '').toLowerCase().includes(termo) ||
          String(p.moeda || '').toLowerCase().includes(termo) ||
          String(p.status || '').toLowerCase().includes(termo) ||
          String(p.descricao || '').toLowerCase().includes(termo) ||
          String(p.origem_cobranca || '').toLowerCase().includes(termo);

        return okCliente && okStatus && okBusca;
      })
      .sort((a, b) => {
        const mesA = String(a.mes_referencia || '');
        const mesB = String(b.mes_referencia || '');
        if (mesA !== mesB) return mesB.localeCompare(mesA);

        const dataA = new Date(a.data_pagamento || a.created_on || 0).getTime();
        const dataB = new Date(b.data_pagamento || b.created_on || 0).getTime();
        return dataB - dataA;
      });
  }, [pagamentos, clientes, clienteId, status, busca]);

  const resumoFinanceiro = useMemo(() => {
    const mesAtual = getMesAtualYYYYMM();

    return linhas.reduce(
      (acc, pagamento) => {
        const statusPagamento = normalizeStatus(pagamento.status);
        const valorRecebido = Number(pagamento.valor || 0);
        const valorPrevisto = Number(
          pagamento.valor_calculado ?? pagamento.valor ?? 0
        );
        const ehMesAtual = String(pagamento.mes_referencia || '') === mesAtual;

        if (statusPagamento === 'pago') {
          acc.recebidoTotal += valorRecebido;
          acc.pagos += 1;
          if (ehMesAtual) acc.recebidoMes += valorRecebido;
        } else if (isStatusAberto(statusPagamento)) {
          acc.aReceberTotal += valorPrevisto;
          acc.emAberto += 1;
          if (ehMesAtual) acc.aReceberMes += valorPrevisto;
        } else if (statusPagamento === 'isento') {
          acc.isentos += 1;
        } else if (statusPagamento === 'cancelado') {
          acc.cancelados += 1;
        }

        return acc;
      },
      {
        recebidoMes: 0,
        recebidoTotal: 0,
        aReceberMes: 0,
        aReceberTotal: 0,
        emAberto: 0,
        pagos: 0,
        isentos: 0,
        cancelados: 0,
      }
    );
  }, [linhas]);

  return (
    <div className="pagamentos-container">
      <h2>Dash de Pagamentos</h2>
      <p>
        Visao financeira das cobrancas, recebimentos e pagamentos em aberto.
      </p>

      {erro && (
        <div
          style={{
            background: 'rgba(255,107,129,.14)',
            color: '#ffdce2',
            border: '1px solid #f5b7b7',
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      <div className="kpi-container">
        <div className="kpi-card">
          <h3>Receita recebida</h3>
          <p>{formatCurrency(resumoFinanceiro.recebidoMes)}</p>
          <small>Total recebido: {formatCurrency(resumoFinanceiro.recebidoTotal)}</small>
        </div>

        <div className="kpi-card">
          <h3>A receber</h3>
          <p>{formatCurrency(resumoFinanceiro.aReceberMes)}</p>
          <small>Total em aberto: {formatCurrency(resumoFinanceiro.aReceberTotal)}</small>
        </div>

        <div className="kpi-card">
          <h3>Pagamentos em aberto</h3>
          <p>{resumoFinanceiro.emAberto}</p>
          <small>Registros filtrados aguardando pagamento</small>
        </div>

        <div className="kpi-card">
          <h3>Pagos</h3>
          <p>{resumoFinanceiro.pagos}</p>
          <small>
            Isentos: {resumoFinanceiro.isentos} | Cancelados: {resumoFinanceiro.cancelados}
          </small>
        </div>
      </div>

      <div className="qt-filter-panel"
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: 12,
          marginBottom: 16,
          alignItems: 'end',
        }}
      >
        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Buscar
          </label>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Cliente, mes, status, metodo, provedor ou descricao..."
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Cliente
          </label>
          <select
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Todos</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id} - {c.nome}
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="todos">Todos</option>
            <option value="Aguardando pagamento">Aguardando pagamento</option>
            <option value="Pago">Pago</option>
            <option value="Isento">Isento</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p>Carregando pagamentos...</p>
      ) : (
        <table className="clientes-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Cliente</th>
              <th>Mês</th>
              <th>Data</th>
              <th>Valor</th>
              <th>Método</th>
              <th>Provedor</th>
              <th>Status</th>
              <th>Origem</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td style={{ textAlign: 'left' }}>
                  <strong>{p.cliente?.nome || p.cliente_id}</strong>
                  <br />
                  <small>{p.cliente?.email || '—'}</small>
                </td>
                <td>{formatMesReferencia(p.mes_referencia)}</td>
                <td>{formatDate(p.data_pagamento)}</td>
                <td>
                  <strong>{formatCurrency(getValorPrincipal(p), p.moeda)}</strong>
                  <br />
                  <small>{getValorLabel(p)}</small>
                </td>
                <td>{p.metodo_pagamento || '—'}</td>
                <td>
                  <strong>{p.provedor_nome || p.provedor_codigo || 'Manual'}</strong>
                  <br />
                  <small>{p.moeda || 'BRL'}</small>
                </td>
                <td>
                  <span
                    className={
                      String(p.status || '').toLowerCase() === 'pago'
                        ? 'badge badge-ativo'
                        : String(p.status || '').toLowerCase() === 'isento'
                        ? 'badge badge-isento'
                        : String(p.status || '').toLowerCase() === 'cancelado'
                        ? 'badge badge-inativo'
                        : 'badge badge-normal'
                    }
                  >
                    {p.status || '—'}
                  </span>
                </td>
                <td>
                  {p.origem_cobranca || '—'}
                  {p.assinatura_id ? (
                    <>
                      <br />
                      <small>Assinatura #{p.assinatura_id}</small>
                    </>
                  ) : null}
                </td>
                <td style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                  {p.descricao || '—'}
                </td>
              </tr>
            ))}

            {linhas.length === 0 && (
              <tr>
                <td colSpan="10">Nenhum pagamento encontrado.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
